import React, { useContext, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Modal, Text12, H4, H5, Button } from '../../atoms';
import { store } from '../../../utils/store';
import { getLanguage } from '../../../utils/language';
import {
  OPTIONAL_CATEGORIES,
  applyConsent,
  clearLegacyConsent,
  denyAll,
  grantAll,
  onOpenPreferences,
  readConsent,
  writeConsent,
} from '../../../utils/consent';

/**
 * The `menu` contentType lives only in the database, so an editor has to add
 * the new keys in the backoffice before they resolve. Until then these apply:
 * the banner has to be correct on the day it deploys, not on the day someone
 * gets round to filling it in.
 *
 * The pre-existing `descriptionCookeis` and `buttonTextCookies` keys are
 * deliberately NOT read. They still hold the old single-button copy ("Aceitar
 * e continuar!"), and the source document asks for that banner to be replaced,
 * not extended — reading them would quietly reinstate the text we were told to
 * drop. New keys are used instead, so the old values stay in the database
 * until an editor clears them.
 */
const defaults = {
  titleCookies: 'Cookies',
  buttonConfigureCookies: 'Configurar',
  buttonRejectAllCookies: 'Rejeitar tudo',
  buttonAcceptAllCookies: 'Aceitar tudo',
  buttonSaveCookies: 'Guardar preferências',
  titleCookiesPanel: 'Configurar cookies',
  lockedCookiesLabel: 'Sempre ativos',
};

const categoryDefaults = {
  necessary: {
    title: 'Necessários',
    description:
      'Permitem o funcionamento básico do site e guardam o seu consentimento de cookies, para que esta escolha não lhe volte a ser pedida em cada visita. Sem eles o site não funciona, pelo que não podem ser desativados.',
  },
  preferences: {
    title: 'Preferências',
    description:
      'Memorizam escolhas que fez, como o idioma do site, para que não tenha de as repetir. Se os recusar, o site continua a funcionar normalmente e passa a abrir sempre no idioma predefinido.',
  },
  analytics: {
    title: 'Analíticos',
    description:
      'Permitem-nos contar visitas e perceber de forma agregada como o site é utilizado, para melhorar o seu desempenho e os seus conteúdos. Utilizamos o Google Analytics.',
  },
};

const defaultDescription = (policyUrl) =>
  'A Medidata esPublico utiliza cookies próprios e de terceiros para fins técnicos, de ' +
  'personalização e analíticos. Pode configurar os tipos de cookies que pretende autorizar. ' +
  'Os cookies classificados como «necessários» não podem ser desativados, uma vez que são ' +
  'essenciais ao funcionamento do website. Clique em «Configurar» para obter mais informações ' +
  `sobre os cookies que utilizamos ou consulte a nossa <a href="${policyUrl}">Política de Cookies</a>.`;

export default function ModalCookies() {

  const { menu } = useContext(store);
  const lang = getLanguage() || 'pt';
  const policyUrl = `/${lang}/legal#cookies`;

  const [open, setOpen] = useState(false);
  const [animation, setAnimation] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [selection, setSelection] = useState(denyAll);

  const text = (key) => menu?.[key] || defaults[key];

  const category = (key, field) =>
    menu?.[`${key}Cookies${field === 'title' ? 'Title' : 'Description'}`] ||
    categoryDefaults[key]?.[field] ||
    '';

  useEffect(() => {
    clearLegacyConsent();

    const stored = readConsent();
    if (stored) {
      // Already answered: honour it silently on every page load.
      setSelection(stored.granted);
      applyConsent(stored.granted);
    } else {
      setOpen(true);
    }
  }, []);

  // The footer's "Configurar cookies" link reaches the panel through here, so
  // withdrawing consent costs the same one click as giving it.
  useEffect(() => onOpenPreferences(() => {
    const stored = readConsent();
    setSelection(stored ? stored.granted : denyAll());
    setShowPreferences(true);
    setOpen(true);
  }), []);

  const afterOpenModal = () => {
    setAnimation(true);
  };

  const close = () => {
    setAnimation(false);
    setTimeout(() => {
      setOpen(false);
      setShowPreferences(false);
    }, 500);
  };

  const decide = (granted) => {
    const record = writeConsent(granted);
    applyConsent(record.granted);
    setSelection(record.granted);
    close();
  };

  const toggle = (key) => {
    setSelection((current) => ({ ...current, [key]: !current[key] }));
  };

  return (
    <Modal isOpen={open} afterOpenModal={afterOpenModal}>
      <Overlay animation={animation} />
      <Content animation={animation}>
        <Structure animation={animation} panel={showPreferences}>

          {!showPreferences &&
            <Banner>
              <Texts>
                <Title color='#368D67'>{text('titleCookies')}</Title>
                <Description
                  dangerouslySetInnerHTML={{
                    __html: menu?.descriptionCookiesBanner || defaultDescription(policyUrl)
                  }}
                />
              </Texts>
              <Actions>
                <Button
                  text={text('buttonConfigureCookies')}
                  color='5D6368'
                  onClick={() => setShowPreferences(true)}
                />
                <Button
                  text={text('buttonRejectAllCookies')}
                  color='5D6368'
                  onClick={() => decide(denyAll())}
                />
                <Button
                  text={text('buttonAcceptAllCookies')}
                  onClick={() => decide(grantAll())}
                />
              </Actions>
            </Banner>
          }

          {showPreferences &&
            <Panel>
              <Title color='#368D67'>{text('titleCookiesPanel')}</Title>

              <Categories>
                <Category>
                  <CategoryHead>
                    <CategoryTitle>{category('necessary', 'title')}</CategoryTitle>
                    <Locked>{text('lockedCookiesLabel')}</Locked>
                  </CategoryHead>
                  <Text12 color='#5D6368'>{category('necessary', 'description')}</Text12>
                </Category>

                {OPTIONAL_CATEGORIES.map(({ key }) => (
                  <Category key={key}>
                    <CategoryHead>
                      <CategoryTitle>{category(key, 'title')}</CategoryTitle>
                      <Switch
                        type='button'
                        role='switch'
                        aria-checked={selection[key] === true}
                        aria-label={category(key, 'title')}
                        checked={selection[key] === true}
                        onClick={() => toggle(key)}
                      >
                        <Knob checked={selection[key] === true} />
                      </Switch>
                    </CategoryHead>
                    <Text12 color='#5D6368'>{category(key, 'description')}</Text12>
                  </Category>
                ))}
              </Categories>

              <Actions>
                <Button
                  text={text('buttonRejectAllCookies')}
                  color='5D6368'
                  onClick={() => decide(denyAll())}
                />
                <Button
                  text={text('buttonSaveCookies')}
                  color='5D6368'
                  onClick={() => decide(selection)}
                />
                <Button
                  text={text('buttonAcceptAllCookies')}
                  onClick={() => decide(grantAll())}
                />
              </Actions>
            </Panel>
          }

        </Structure>
      </Content>
    </Modal>
  )
}

const Overlay = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: rgba(0,0,0,0);
  transition: all 0.5s ease;

  ${({ animation }) => animation && css`
    background-color: rgba(0,0,0,0.5);
  `}
`;
const Structure = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 60px 125px;
  background-color: #fff;
  transform: translateY(100%);
  transition: all 0.5s ease;

  ${({ panel }) => panel && css`
    max-height: 90vh;
    overflow-y: auto;
  `}

  @media screen and (max-width: 1280px) {
    padding: 60px 90px;
  }
  @media screen and (max-width: 768px) {
    padding: 60px 60px;
  }
  @media screen and (max-width: 578px) {
    padding: 45px 30px;
  }
`;
const Banner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  @media screen and (max-width: 768px) {
    flex-direction: column;
  }
`;
const Panel = styled.div`
  display: flex;
  flex-flow: column;
`;
const Texts = styled.div`
  margin-right: 100px;
  flex: 1;
  @media screen and (max-width: 768px) {
    margin-right: 0;
  }
`;
const Title = styled(H4)`
  margin-bottom: 12px;
`;
const Description = styled(Text12)`
  a {
    color: #368D67;
    text-decoration: underline;
  }
`;
const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
  @media screen and (max-width: 768px) {
    margin-top: 24px;
    width: 100%;
    flex-direction: column;
  }
`;
const Categories = styled.div`
  display: flex;
  flex-flow: column;
  gap: 24px;
  margin: 24px 0 32px;
`;
const Category = styled.div`
  display: flex;
  flex-flow: column;
  padding-bottom: 24px;
  border-bottom: 1px solid #E6E8EA;
`;
const CategoryHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;
const CategoryTitle = styled(H5)`
  color: #5D6368;
`;
const Locked = styled(Text12)`
  color: #9AA0A6;
`;
const Switch = styled.button`
  position: relative;
  width: 44px;
  height: 24px;
  padding: 0;
  border: 1px solid ${({ checked }) => checked ? '#368D67' : '#C4C8CC'};
  border-radius: 12px;
  background-color: ${({ checked }) => checked ? '#368D67' : '#fff'};
  transition: all 0.3s ease;
  flex-shrink: 0;
  :hover {
    cursor: pointer;
  }
`;
const Knob = styled.span`
  position: absolute;
  top: 3px;
  left: ${({ checked }) => checked ? '23px' : '3px'};
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${({ checked }) => checked ? '#fff' : '#C4C8CC'};
  transition: all 0.3s ease;
`;
const Content = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-end;
  opacity: 0;
  transition: all 0.5s ease;

  ${({ animation }) => animation && css`
    opacity: 1;

    ${Structure} {
      transform: translateY(0);
    }
  `};
`;
